import { type FC, useState, useEffect, useCallback } from 'react';
import { useDibsContracts } from 'dibs-crypto-utils/exports/useDibsContracts';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { Button } from 'dibs-elements/exports/Button';
import CirclePlus from 'dibs-icons/exports/legacy/CirclePlus';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ModalBody } from 'dibs-elements/exports/ModalBody';

import { BarHeader } from 'dibs-elements/exports/BarHeader';

import { FormattedMessage } from 'dibs-react-intl';
import styles from './styles/CreateNftButton.scss';

export const CreateNftButton: FC<{
    fullWidth?: boolean;
    persistedAddress: string;
    buttonSize: 'small' | 'medium' | 'large';
    hasProfile: boolean;
}> = ({ fullWidth, persistedAddress, buttonSize, hasProfile }) => {
    const { library } = useWeb3Connection();
    const [contracts] = useDibsContracts(library);
    const [modalOpen, setIsModalOpen] = useState(false);
    const [canMint, setCanMint] = useState(false);

    const checkMinterRole = useCallback(async () => {
        if (contracts?.tokenV2) {
            const token = contracts.tokenV2;
            // eslint-disable-next-line new-cap
            const minterRole = await token.MINTER_ROLE();
            setCanMint(!!(await token.hasRole(minterRole, persistedAddress)));
        }
    }, [contracts, persistedAddress]);
    useEffect(() => {
        checkMinterRole();
    }, [checkMinterRole, contracts]);
    return (
        <>
            <Button
                className={`${styles.createNftButton} ${styles.important}`}
                size={buttonSize}
                href={canMint && hasProfile ? '/nft-create' : undefined}
                onClick={() => {
                    if (!canMint || !hasProfile) {
                        setIsModalOpen(true);
                    }
                }}
                fullWidth={fullWidth}
            >
                <CirclePlus className={styles.plusIcon} />
                <FormattedMessage defaultMessage="Create" id="nft.connectWallet.create" />
            </Button>
            <ModalContainer isOpen={modalOpen} onClose={() => setIsModalOpen(false)}>
                <ModalCloseButton onClick={() => setIsModalOpen(false)} />
                <BarHeader
                    title={
                        !hasProfile ? (
                            <FormattedMessage
                                defaultMessage="Create a Profile First"
                                id="nft.createProfile.header"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Apply to Sell NFTs on 1stDibs"
                                id="nft.applyToSell.header"
                            />
                        )
                    }
                />
                <ModalBody>
                    <p className={styles.applyBody}>
                        {!hasProfile ? (
                            <FormattedMessage
                                defaultMessage="In order to mint NFTs on 1stDibs, you'll need to create a profile first."
                                id="nft.createProfile.body"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="1stDibs is a leading destination for rare, collectible design and a curated platform. Before listing NFTs on our site, you will need to submit a creator application which will be reviewed by our team. Once approved, you’ll be able to mint tokens on 1stDibs."
                                id="nft.applyToSell.body"
                            />
                        )}
                    </p>
                    <Button
                        fullWidth
                        href={!hasProfile ? '/profile-edit' : '/info/nft-creator-application/'}
                    >
                        {!hasProfile ? (
                            <FormattedMessage
                                defaultMessage="Create Profile"
                                id="nft.createProfile.button"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Apply to Sell"
                                id="nft.applyToSell.button"
                            />
                        )}
                    </Button>
                </ModalBody>
            </ModalContainer>
        </>
    );
};
